import { Link } from "react-router-dom";
import { Activity, ArrowUpRight } from "lucide-react";
import { cn } from "@/lib/utils";

/* Logo -------------------------------------------------------------------- */
export function Logo({ light = false, className = "" }) {
  return (
    <Link to="/" className={cn("flex items-center gap-2.5 group", className)} data-testid="brand-logo">
      <span className="grid h-7 w-7 place-items-center bg-orange-500 rounded-[6px]">
        <Activity className="h-4 w-4 text-white" strokeWidth={2.6} />
      </span>
      <span className={cn("text-lg font-bold tracking-tight", light ? "text-white" : "text-[#0a0a0a]")}>
        Routa
      </span>
    </Link>
  );
}

/* Monospace eyebrow tag --------------------------------------------------- */
export function Eyebrow({ children, className = "" }) {
  return <span className={cn("eyebrow", className)}>{children}</span>;
}

/* Numbered section header with rule + right context tag ------------------- */
export function SectionEyebrow({ index, title, tag, light = false, className = "" }) {
  return (
    <div className={cn("flex items-center gap-4", className)}>
      <span className={cn("font-mono text-[11px] tracking-[0.18em] uppercase", light ? "text-white/70" : "text-gray-400")}>
        [{index}] {title}
      </span>
      <span className={cn("h-px flex-1", light ? "bg-white/20" : "bg-gray-200")} />
      {tag && (
        <span className={cn("font-mono text-[11px] tracking-[0.18em] uppercase", light ? "text-white/50" : "text-gray-400")}>
          {tag}
        </span>
      )}
    </div>
  );
}

/* Form section number label ("01 · OPERATOR IDENTITY") -------------------- */
export function FieldGroupLabel({ number, label }) {
  return (
    <div className="font-mono text-[11px] tracking-[0.18em] uppercase text-gray-500 mb-5">
      {number} · {label}
    </div>
  );
}

/* Buttons ----------------------------------------------------------------- */
export function PrimaryButton({ as: As = "button", orange = false, arrow = false, className = "", children, ...props }) {
  return (
    <As
      className={cn(
        "inline-flex items-center justify-center gap-2 px-6 py-3 text-sm font-semibold transition-colors",
        orange ? "bg-orange-500 text-white hover:bg-orange-600" : "bg-[#0a0a0a] text-white hover:bg-[#262626]",
        className
      )}
      {...props}
    >
      {children}
      {arrow && <ArrowUpRight className="h-4 w-4" strokeWidth={2.4} />}
    </As>
  );
}

export function SecondaryButton({ as: As = "button", className = "", children, ...props }) {
  return (
    <As
      className={cn(
        "inline-flex items-center justify-center gap-2 px-6 py-3 text-sm font-semibold border border-[#0a0a0a] text-[#0a0a0a] bg-white hover:bg-gray-50 transition-colors",
        className
      )}
      {...props}
    >
      {children}
    </As>
  );
}

/* Status pill ------------------------------------------------------------- */
export const STATUS_META = {
  created: { label: "Unassigned", cls: "bg-white text-gray-500 border border-gray-300" },
  assigned: { label: "Assigned", cls: "bg-gray-100 text-[#0a0a0a] border border-gray-200" },
  picked_up: { label: "Picked up", cls: "bg-orange-500 text-white" },
  en_route: { label: "In transit", cls: "bg-orange-500 text-white" },
  delivered: { label: "Delivered", cls: "bg-[#0a0a0a] text-white" },
  exception: { label: "Exception", cls: "bg-white text-orange-600 border border-orange-500" },
  pending: { label: "Pending", cls: "bg-gray-100 text-gray-600 border border-gray-200" },
  under_review: { label: "Under review", cls: "bg-white text-orange-600 border border-orange-500" },
  approved: { label: "Approved", cls: "bg-[#0a0a0a] text-white" },
  rejected: { label: "Rejected", cls: "bg-white text-orange-600 border border-orange-500" },
  active: { label: "Active", cls: "bg-[#0a0a0a] text-white" },
  suspended: { label: "Suspended", cls: "bg-white text-orange-600 border border-orange-500" },
  applicant: { label: "Applicant", cls: "bg-gray-100 text-gray-600 border border-gray-200" },
};

export function StatusPill({ status, className = "" }) {
  const meta = STATUS_META[status] || { label: status, cls: "bg-gray-100 text-gray-600 border border-gray-200" };
  return (
    <span
      data-testid={`status-pill-${status}`}
      className={cn(
        "inline-flex items-center rounded-full px-2.5 py-0.5 font-mono text-[10px] uppercase tracking-[0.1em] whitespace-nowrap",
        meta.cls,
        className
      )}
    >
      {meta.label}
    </span>
  );
}

/* Stat block -------------------------------------------------------------- */
export function Stat({ label, value, sub, className = "" }) {
  return (
    <div className={cn("", className)}>
      <div className="font-mono text-[11px] tracking-[0.14em] uppercase text-gray-400 mb-2">{label}</div>
      <div className="text-2xl font-bold text-[#0a0a0a] tracking-tight">{value}</div>
      {sub && <div className="text-xs text-gray-500 mt-1">{sub}</div>}
    </div>
  );
}
